import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'success',
  duration = 4000,
  onClose
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 flex-shrink-0" />;
      case 'error':
        return <XCircle className="h-5 w-5 flex-shrink-0" />;
      case 'info':
        return <AlertCircle className="h-5 w-5 flex-shrink-0" />;
    }
  };

  const getStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-emerald-50 dark:bg-emerald-950 border-emerald-500 text-emerald-800 dark:text-emerald-100';
      case 'error':
        return 'bg-red-50 dark:bg-red-950 border-red-500 text-red-800 dark:text-red-100';
      case 'info':
        return 'bg-blue-50 dark:bg-blue-950 border-blue-500 text-blue-800 dark:text-blue-100';
    }
  };

  return (
    <div
      className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down`}
      style={{
        animation: 'slideDown 0.3s ease-out'
      }}
    >
      <div
        className={`${getStyles()} border-l-4 rounded-lg shadow-2xl p-4 pr-12 min-w-[320px] max-w-md backdrop-blur-sm`}
      >
        <div className="flex items-center space-x-3">
          {getIcon()}
          <p className="font-semibold text-sm">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-current opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
